import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import HrDashboardLayout from '../components/layouts/HrDashboardLayout';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import Input from '../components/atoms/Input';
import SessionManager from '../utils/sessionManager';
import { API_BASE_URL } from '../config';
import * as secureApiService from '../services/secureApi';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const HrCandidatesPage = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [inviteModalOpen, setInviteModalOpen] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [candidateApplications, setCandidateApplications] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [inviting, setInviting] = useState(false);
  const [securityStatus, setSecurityStatus] = useState(null); // Track security connection

  // Get HR company name
  const getHrCompanyName = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return hrSession?.company || hrSession?.profile?.company_name || 'Company';
  };

  const hrCompanyName = getHrCompanyName();

  // Check HR authentication
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/hr_login');
      return;
    }
  }, [navigate]);

  // Fetch candidates using secure API
  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      // Build filters object
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (locationFilter) filters.location = locationFilter;
      if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
      
      // Add company filter for HR
      if (hrCompanyName) {
        filters.company = hrCompanyName;
      }
      
      // Handle experience filter
      if (experienceFilter && experienceFilter !== 'all') {
        if (experienceFilter === 'fresher') {
          filters.max_experience = '1';
        } else if (experienceFilter === '1-3') {
          filters.min_experience = '1';
          filters.max_experience = '3';
        } else if (experienceFilter === '3-5') {
          filters.min_experience = '3';
          filters.max_experience = '5';
        } else if (experienceFilter === '5+') {
          filters.min_experience = '5';
        }
      }

      console.log('🔐 Fetching candidates with secure API...');
      console.log('📋 Filters:', filters);

      // Use secure API service
      const data = await secureApiService.getCandidates(filters);
      
      console.log('✅ Secure candidates data received:', data);
      console.log('🔒 Data is encrypted:', data.security?.encrypted || false);
      
      setCandidates(data.candidates || []);
    } catch (error) {
      console.error('❌ Error fetching candidates:', error);
      setCandidates([]);
      
      // Show user-friendly error message
      if (error.message.includes('No session found') || error.message.includes('authentication required')) {
        alert('Session expired. Please log in again.');
        navigate('/login');
      } else if (error.message.includes('Failed to get JWT token')) {
        alert('Authentication failed. Please try logging in again.');
      } else {
        alert('Failed to load candidates. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, locationFilter, experienceFilter, statusFilter, hrCompanyName, navigate]);

  // Fetch jobs for invite modal
  const fetchJobs = useCallback(async () => {
    try {
      console.log('🔍 Fetching jobs for company:', hrCompanyName);
      const url = `${API_BASE_URL}/jobs?company=${encodeURIComponent(hrCompanyName)}`;
      console.log('📡 Jobs API URL:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Jobs fetch failed:', response.status, errorText);
        throw new Error('Failed to fetch jobs');
      }
      
      const data = await response.json();
      console.log('✅ Jobs fetched - Raw response:', data);
      console.log('✅ Is array?', Array.isArray(data));
      
      // The API returns an array directly, not {jobs: [...]}
      const jobsArray = Array.isArray(data) ? data : (data.jobs || []);
      console.log('📊 Total jobs:', jobsArray.length);
      console.log('🟢 Active jobs:', jobsArray.filter(j => j.status === 'Active').length || 0);
      console.log('📋 All jobs:', jobsArray);
      setJobs(jobsArray);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    }
  }, [hrCompanyName]);

  useEffect(() => {
    fetchCandidates();
    fetchJobs();
    
    // Test secure connection
    const testSecurity = async () => {
      try {
        const result = await secureApiService.testConnection();
        setSecurityStatus(result);
        console.log('🔐 Security test result:', result);
      } catch (error) {
        console.error('❌ Security test failed:', error);
        setSecurityStatus({ success: false, error: error.message });
      }
    };
    
    testSecurity();
  }, [fetchCandidates, fetchJobs]);

  const handleSearch = () => {
    fetchCandidates();
  };

  const handleInviteClick = async (candidate) => {
    console.log('🎯 Opening invite modal for candidate:', candidate);
    console.log('📋 Current jobs state:', jobs);
    console.log('🏢 HR Company Name:', hrCompanyName);
    
    setInviteModalOpen(candidate);
    setSelectedJob('');
    
    // Decrypt candidate ID for API calls (email is already plain text)
    try {
      const realCandidateId = await secureApiService.decryptValue(candidate.id);
      console.log('🔓 Decrypted candidate ID:', realCandidateId);
      
      // Fetch candidate's applications using real ID
      const response = await fetch(`${API_BASE_URL}/candidates/${realCandidateId}/applications`, { headers: buildAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Candidate applications:', data.applications);
        setCandidateApplications(data.applications || []);
      }
    } catch (error) {
      console.error('❌ Error fetching candidate applications:', error);
      setCandidateApplications([]);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteModalOpen) return;
    
    setInviting(true);
    try {
      // Decrypt candidate ID for the API call
      const realCandidateId = await secureApiService.decryptValue(inviteModalOpen.id);
      console.log('🔓 Using decrypted candidate ID for invite:', realCandidateId);
      
      // Build query parameters (backend expects Query params, not JSON body)
      const params = new URLSearchParams({
        candidate_id: realCandidateId,  // Use decrypted ID
        company_name: hrCompanyName,
        frontend_url: window.location.origin
      });
      
      if (selectedJob) {
        params.append('job_id', selectedJob);
      }

      console.log('📤 Sending invite with params:', params.toString());

      const response = await fetch(`${API_BASE_URL}/candidates/invite-assessment?${params.toString()}`, {
        method: 'POST',
        headers: { ...buildAuthHeaders(), 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Invite failed:', response.status, errorData);
        throw new Error('Failed to send invitation');
      }
      
      const data = await response.json();
      console.log('✅ Invite sent successfully:', data);
      alert(`Assessment invitation sent to ${data.candidate_name}`);
      setInviteModalOpen(null);
      setSelectedJob('');
    } catch (error) {
      console.error('❌ Error sending invitation:', error);
      if (error.message.includes('Decryption failed')) {
        alert('Failed to process candidate data. Please refresh and try again.');
      } else {
        alert('Failed to send invitation. Please try again.');
      }
    } finally {
      setInviting(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'Not Applied') return 'bg-gray-100 text-gray-700';
    if (status.includes('Interview Taken')) return 'bg-green-100 text-green-700';
    if (status.includes('Pending')) return 'bg-amber-100 text-amber-700';
    return 'bg-blue-100 text-blue-700';
  };

  return (
    <HrDashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">All Candidates</h1>
            <p className="text-gray-600 mt-1">Search, filter, and invite candidates for assessments</p>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-4">
            <div className="lg:col-span-2">
              <Input
                placeholder="Search by name, email, skills, or projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Input
              placeholder="Filter by location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            />
            <Button onClick={handleSearch} variant="primary">
              <Icon name="search" size={20} className="mr-2" />
              Search
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Experience:</span>
              {['all', 'fresher', '1-3', '3-5', '5+'].map((exp) => (
                <button
                  key={exp}
                  onClick={() => setExperienceFilter(exp)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    experienceFilter === exp
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {exp === 'all' ? 'All' : exp === 'fresher' ? 'Fresher' : `${exp} years`}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 ml-4">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              {[
                { value: 'all', label: 'All' },
                { value: 'not_applied', label: 'Not Applied' },
                { value: 'applied', label: 'Applied' },
                { value: 'interview_taken', label: 'Interview Taken' }
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setStatusFilter(status.value)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    statusFilter === status.value
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
        </div>

      {/* Candidates Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600">Loading candidates...</p>
            </div>
          ) : candidates.length === 0 ? (
            <div className="p-12 text-center">
              <Icon name="person_search" size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No candidates found</p>
              <p className="text-sm text-gray-500 mt-2">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Experience
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ATS Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {candidates.map((candidate) => (
                    <tr
                      key={candidate.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={async () => {
                        try {
                          console.log('🔓 Decrypting candidate ID for navigation...');
                          const realId = await secureApiService.decryptValue(candidate.id);
                          console.log('✅ Navigating to candidate profile:', realId, candidate);
                          navigate(`/hr/candidate-profile/${realId}`);
                        } catch (error) {
                          console.error('❌ Failed to decrypt candidate ID:', error);
                          alert('Failed to load candidate profile. Please try again.');
                        }
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-semibold">
                              {candidate.name ? candidate.name.charAt(0).toUpperCase() : '?'}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{candidate.name || 'Unknown'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {candidate.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {candidate.experience || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {candidate.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(candidate.status)}`}>
                          {candidate.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {candidate.ats_score ? (
                          <span className={`font-semibold ${candidate.ats_score >= 70 ? 'text-green-600' : 'text-amber-600'}`}>
                            {candidate.ats_score.toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInviteClick(candidate);
                          }}
                        >
                          <Icon name="mail" size={16} className="mr-1" />
                          Invite
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* Results count */}
      {!loading && candidates.length > 0 && (
        <div className="mt-4 text-center text-sm text-gray-600">
          Showing {candidates.length} candidate{candidates.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Invite Modal */}
      {inviteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Invite for Assessment</h3>
              <button
                onClick={() => setInviteModalOpen(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="close" size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Sending invitation to: <span className="font-semibold">{inviteModalOpen.name}</span>
              </p>
              <p className="text-sm text-gray-600">
                Email: <span className="font-semibold">{inviteModalOpen.email}</span>
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Job Position (Optional)
              </label>
              <select
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">General Assessment</option>
                {(() => {
                  console.log('🎨 Rendering job options. Total jobs:', jobs.length);
                  const activeJobs = jobs.filter(job => job.status === 'Active');
                  console.log('🟢 Active jobs to display:', activeJobs.length, activeJobs);
                  return activeJobs.map((job) => {
                    // Check if candidate already applied to this job
                    const application = candidateApplications.find(app => app.job_id === job.id);
                    const hasApplied = !!application;
                    const atsScore = application?.ats_score;
                    
                    console.log(`  - Job: ${job.title}, Applied: ${hasApplied}, ATS: ${atsScore}`);
                    
                    return (
                      <option key={job.id} value={job.id}>
                        {job.title}
                        {hasApplied && atsScore ? ` (Applied - ATS: ${atsScore.toFixed(1)}%)` : hasApplied ? ' (Applied)' : ''}
                      </option>
                    );
                  });
                })()}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Jobs marked with "Applied" show the candidate already applied with their ATS score
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setInviteModalOpen(null)}
                className="flex-1"
                disabled={inviting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSendInvite}
                className="flex-1"
                disabled={inviting}
              >
                {inviting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Candidate Detail Modal */}
      {selectedCandidate && !inviteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{selectedCandidate.name}</h3>
              <button
                onClick={() => setSelectedCandidate(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="close" size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-base font-medium">{selectedCandidate.email}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Experience</p>
                <p className="text-base font-medium">{selectedCandidate.experience}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="text-base font-medium">{selectedCandidate.location}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedCandidate.status)}`}>
                  {selectedCandidate.status}
                </span>
              </div>

              {selectedCandidate.ats_score && (
                <div>
                  <p className="text-sm text-gray-500">ATS Score</p>
                  <p className={`text-2xl font-bold ${selectedCandidate.ats_score >= 70 ? 'text-green-600' : 'text-amber-600'}`}>
                    {selectedCandidate.ats_score.toFixed(1)}%
                  </p>
                </div>
              )}

              {selectedCandidate.skills && selectedCandidate.skills.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200">
                <Button
                  variant="primary"
                  onClick={() => {
                    handleInviteClick(selectedCandidate);
                    setSelectedCandidate(null);
                  }}
                  className="w-full"
                >
                  <Icon name="mail" size={20} className="mr-2" />
                  Invite for Assessment
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </HrDashboardLayout>
  );
};

export default HrCandidatesPage;
